import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import Slider, { Settings as ReactSlickProps } from "react-slick"
import {
  Box,
  BoxProps,
  Image,
  StyleProps,
  Text,
  UnorderedList,
} from "@chakra-ui/react"
import { DepositForAccount } from "@mezo/api"

import checkIconBlack from "#/assets/check-icon-black.svg"

const MAX_VISIBLE_DOTS = 14

type DepositsCarouselProps = BoxProps &
  Pick<ReactSlickProps, "centerPadding"> & {
    mainLabelText?: string
    deposits: DepositForAccount[]
    initialDepositUuid?: string
    renderDepositItem: (
      deposit: DepositForAccount,
      isActive: boolean,
      mainItemContainerProps: StyleProps,
    ) => React.ReactNode
    onItemChange?: (previousIndex: number, nextIndex: number) => void
  }

export default function DepositCarousel({
  mainLabelText,
  deposits,
  initialDepositUuid,
  centerPadding,
  onItemChange,
  renderDepositItem,
  ...props
}: DepositsCarouselProps) {
  const [activeSlide, setActiveSlide] = useState(0)
  const sliderRef = useRef(null)

  const initialDepositIndex = useMemo(
    () => deposits.findIndex((deposit) => deposit.id === initialDepositUuid),
    [initialDepositUuid, deposits],
  )

  useEffect(() => {
    if (initialDepositIndex >= 0) {
      // @ts-expect-error goes to specific item on carousel display
      sliderRef.current.slickGoTo(initialDepositIndex, true)
    }
  }, [initialDepositUuid, initialDepositIndex])

  // Needed to calculate padding for `mainLabelText` so that it's places right
  // on top of currently centered item.
  const mainItemContainerPadding = "5px"

  const carouselSpeedInMs = 500

  const sliderSettings: ReactSlickProps = {
    centerMode: true,
    centerPadding: centerPadding ?? "50px",
    dots: deposits.length > 1,
    dotsClass: "slick-dots custom-dots",
    infinite: false,
    focusOnSelect: true,
    speed: carouselSpeedInMs,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    onInit: () => {
      setActiveSlide(0)
      if (onItemChange) onItemChange(0, 0)
    },
    beforeChange: (current: number, next: number) => {
      setActiveSlide(next)
      if (onItemChange) onItemChange(current, next)
    },

    appendDots: (dots: ReactNode) => (
      <Box padding="12px">
        <UnorderedList
          display="flex"
          justifyContent={
            deposits.length > MAX_VISIBLE_DOTS ? "flex-start" : "center"
          }
        >
          {dots}
        </UnorderedList>
      </Box>
    ),

    customPaging: (i: number) =>
      activeSlide === i ? (
        <Box
          height="20px"
          width="20px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Image src={checkIconBlack} height="16px" width="16px" />
        </Box>
      ) : (
        <Box
          height="20px"
          width="20px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            height="3px"
            width="10px"
            backgroundColor="primary.black.30"
            borderRadius="2px"
          />
        </Box>
      ),
  }

  /**
   * This calculation is needed so that the main label is displayed exactly
   * above the currently centered (active) item
   */
  const mainLabelPaddingX = useMemo(
    () =>
      (
        parseInt(sliderSettings.centerPadding!, 10) +
        parseInt(mainItemContainerPadding, 10)
      )
        .toString()
        .concat("px"),
    [sliderSettings.centerPadding],
  )

  /**
   * If there are many deposits we need to apply custom styles and scrollbar
   * so it does not overflow the modal container
   */
  const customDotsStyles = useMemo(
    () =>
      deposits.length > MAX_VISIBLE_DOTS
        ? {
            sx: {
              ".custom-dots": {
                maxW: "414px",
                marginInline: "54px",
                overflow: "auto",
                px: 0,
              },
              ".custom-dots > ul": {
                mx: 0,
              },
              ".custom-dots::-webkit-scrollbar": {
                "-webkit-appearance": "none",
                height: "7px",
                bg: "primary.black.10",
                borderRadius: "12px",
              },
              ".custom-dots::-webkit-scrollbar-thumb": {
                borderRadius: "12px",
                bg: "primary.black.100",
              },
            },
          }
        : {},
    [deposits.length],
  )

  return (
    <Box pt="10px" pb="30px" {...props} {...customDotsStyles}>
      {mainLabelText && (
        <Text
          variant="label"
          fontSize="12px"
          fontWeight={500}
          color="primary.black.60"
          fontFamily="riforma.medium"
          px={mainLabelPaddingX}
        >
          {mainLabelText}
        </Text>
      )}
      <Slider {...sliderSettings} ref={sliderRef}>
        {deposits.map((deposit, i) =>
          renderDepositItem(deposit, activeSlide === i, {
            padding: mainItemContainerPadding,
          }),
        )}
      </Slider>
    </Box>
  )
}
